// =============================================
// ENHANCED EXPORT CONTROLS COMPONENT
// components/reports/ExportControls.js
// =============================================

import React, { useState, useEffect } from 'react'
import { Download, Settings, FileText, FileSpreadsheet, ChevronDown } from 'lucide-react'
import { ExportSettingsModal } from './ExportSettingsModal'
import { generatePDFReport } from '../../utils/pdfExport'
import { generateComprehensiveExcel } from '../../utils/excelExportUtils'
import { supabase } from '../../lib/supabase'

export const ExportControls = ({ activeTab, data, filters, currentTenant }) => {
  const [isExporting, setIsExporting] = useState(false)
  const [exportType, setExportType] = useState('pdf') // 'pdf' or 'excel'
  const [showSettings, setShowSettings] = useState(false)
  const [showExportMenu, setShowExportMenu] = useState(false)
  const [exportSettings, setExportSettings] = useState(null)
  const [loadingSettings, setLoadingSettings] = useState(true)

  // Excel-specific configuration
  const [excelConfig, setExcelConfig] = useState({
    groupBy: 'client', // client, project, user
    includeDetails: true,
    includeSummary: true,
    includeAllViews: false
  })

  useEffect(() => {
    if (currentTenant) {
      loadExportSettings()
    }
  }, [currentTenant])

  const loadExportSettings = async () => {
    try {
      setLoadingSettings(true)
      
      const { data: tenantData, error } = await supabase
        .from('tenants')
        .select('report_settings, logo_url, company_address, phone, email, website, primary_color, accent_color, name')
        .eq('id', currentTenant.id)
        .single()

      if (error && error.code !== 'PGRST116') {
        throw error
      }

      if (tenantData) {
        const settings = {
          template: tenantData.report_settings?.template || 'professional',
          branding: {
            companyName: currentTenant.name || tenantData.name,
            logo: tenantData.logo_url || '',
            phone: tenantData.phone || '',
            email: tenantData.email || '',
            website: tenantData.website || '',
            address: tenantData.company_address || '',
            primaryColor: tenantData.primary_color || '#3B82F6',
            accentColor: tenantData.accent_color || '#10B981'
          },
          confidentiality: {
            enabled: tenantData.report_settings?.confidentiality?.enabled ?? true,
            level: tenantData.report_settings?.confidentiality?.level || 'confidential',
            customText: tenantData.report_settings?.confidentiality?.customText || '',
            showWatermark: tenantData.report_settings?.confidentiality?.showWatermark || false,
            includeDisclaimer: tenantData.report_settings?.confidentiality?.includeDisclaimer ?? true
          },
          dateRange: {
            showOnCover: tenantData.report_settings?.dateRange?.showOnCover ?? true,
            format: tenantData.report_settings?.dateRange?.format || 'full',
            customFormat: tenantData.report_settings?.dateRange?.customFormat || 'MMMM DD, YYYY'
          },
          customSections: tenantData.report_settings?.customSections || []
        }
        setExportSettings(settings)
      } else {
        const defaultSettings = {
          template: 'professional',
          branding: {
            companyName: currentTenant.name || 'Company',
            logo: '',
            phone: '',
            email: '',
            website: '',
            address: '',
            primaryColor: '#3B82F6',
            accentColor: '#10B981'
          },
          confidentiality: {
            enabled: true,
            level: 'confidential',
            customText: '',
            showWatermark: false,
            includeDisclaimer: true
          },
          dateRange: {
            showOnCover: true,
            format: 'full',
            customFormat: 'MMMM DD, YYYY'
          },
          customSections: []
        }
        setExportSettings(defaultSettings)
      }
    } catch (error) {
      console.error('Error loading export settings:', error)
      setExportSettings({
        template: 'professional',
        branding: { 
          companyName: currentTenant?.name || 'Company',
          primaryColor: '#3B82F6',
          accentColor: '#10B981'
        },
        confidentiality: { enabled: false },
        dateRange: { showOnCover: true, format: 'full' },
        customSections: []
      })
    } finally {
      setLoadingSettings(false)
    }
  }

  const handleExportPDF = async () => {
    if (!data || data.length === 0) {
      alert('No data available to export. Please ensure you have time entries for the selected date range.')
      return
    }

    if (!exportSettings) {
      alert('Export settings are not loaded. Please try refreshing the page or configure export settings first.')
      return
    }

    setIsExporting(true)
    
    try {
      const reportConfig = {
        reportType: activeTab,
        data: data,
        filters: {
          ...filters,
          startDate: filters.startDate,
          endDate: filters.endDate,
          clientName: getFilterDisplayName('client', filters.clientId),
          projectName: getFilterDisplayName('project', filters.projectId),
          userName: getFilterDisplayName('user', filters.userId)
        },
        tenant: currentTenant
      }

      await generatePDFReport(reportConfig, exportSettings)
      setShowExportMenu(false)
      
    } catch (error) {
      console.error('Export error:', error)
      alert('An error occurred while exporting the PDF. Please try again or contact support if the problem persists.')
    } finally {
      setIsExporting(false)
    }
  }

  const handleExportExcel = async () => {
    if (!data || data.length === 0) {
      alert('No data available to export. Please ensure you have time entries for the selected date range.')
      return
    }

    setIsExporting(true)
    
    try {
      const exportConfig = {
        groupBy: excelConfig.groupBy,
        includeDetails: excelConfig.includeDetails,
        includeSummary: excelConfig.includeSummary,
        includeAllViews: excelConfig.includeAllViews,
        filters: {
          startDate: filters.startDate,
          endDate: filters.endDate,
          clientId: filters.clientId,
          projectId: filters.projectId,
          userId: filters.userId,
          status: filters.status
        }
      }

      await generateComprehensiveExcel(
        currentTenant.id,
        exportConfig,
        currentTenant.name
      )
      
      setShowExportMenu(false)
      alert('Excel report generated successfully! Check your downloads folder.')
      
    } catch (error) {
      console.error('Excel export error:', error)
      alert('An error occurred while exporting to Excel. Please try again or contact support if the problem persists.')
    } finally {
      setIsExporting(false)
    }
  }

  const getFilterDisplayName = (type, id) => {
    if (!id || id === 'all') return null
    return `Selected ${type}`
  }

  const getDataSummary = () => {
    if (!data || data.length === 0) return 'No data'
    
    const totalHours = data.reduce((sum, entry) => sum + (entry.duration_hours || 0), 0)
    const billableHours = data.reduce((sum, entry) => sum + (entry.is_billable ? (entry.duration_hours || 0) : 0), 0)
    
    return `${data.length} entries, ${totalHours.toFixed(1)}h total, ${billableHours.toFixed(1)}h billable`
  }

  if (loadingSettings) {
    return (
      <div className="flex items-center space-x-3">
        <div className="animate-spin w-5 h-5 border-2 border-slate-300 border-t-blue-600 rounded-full"></div>
        <span className="text-sm text-slate-500">Loading export settings...</span>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Control Buttons */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {/* Settings Button */}
          <button
            onClick={() => setShowSettings(true)}
            className="flex items-center space-x-2 px-4 py-2 text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 hover:text-slate-900 transition-colors"
          >
            <Settings className="w-4 h-4" />
            <span>Settings</span>
          </button>
        </div>

        {/* Export Dropdown Menu */}
        <div className="relative">
          <button
            onClick={() => setShowExportMenu(!showExportMenu)}
            disabled={isExporting || !data || data.length === 0}
            className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            {isExporting ? (
              <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            <span>{isExporting ? 'Exporting...' : 'Export'}</span>
            {!isExporting && <ChevronDown className="w-4 h-4" />}
          </button>

          {/* Export Dropdown */}
          {showExportMenu && !isExporting && (
            <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-slate-200 z-50">
              {/* PDF Export Option */}
              <div className="p-4 border-b border-slate-200">
                <button
                  onClick={handleExportPDF}
                  className="w-full flex items-start space-x-3 p-3 rounded-lg hover:bg-slate-50 transition-colors text-left"
                >
                  <FileText className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="font-medium text-slate-900">Export as PDF</div>
                    <div className="text-sm text-slate-600 mt-1">
                      Professional formatted report with your branding
                    </div>
                  </div>
                </button>
              </div>

              {/* Excel Export Option */}
              <div className="p-4">
                <div className="mb-3">
                  <button
                    onClick={handleExportExcel}
                    className="w-full flex items-start space-x-3 p-3 rounded-lg hover:bg-slate-50 transition-colors text-left"
                  >
                    <FileSpreadsheet className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="font-medium text-slate-900">Export as Excel</div>
                      <div className="text-sm text-slate-600 mt-1">
                        Detailed spreadsheet with drill-down hierarchy
                      </div>
                    </div>
                  </button>
                </div>
				 {/* Data Summary Info */}
      {data && data.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-blue-800 font-medium">
              {getDataSummary()}
            </span>
            
          </div>
        </div>
      )}

      {/* Warnings */}
      {(!data || data.length === 0) && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <div className="flex items-center space-x-2 text-yellow-800">
            <div className="w-4 h-4 rounded-full bg-yellow-400 flex items-center justify-center">
              <span className="text-xs font-bold text-yellow-900">!</span>
            </div>
            <span className="text-sm font-medium">
              No data available for export. Please adjust your filters or date range.
            </span>
          </div>
        </div>
      )}

                {/* Excel Configuration */}
                <div className="pl-8 space-y-3 border-l-2 border-green-200">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2">
                      Primary Grouping
                    </label>
                    <select
                      value={excelConfig.groupBy}
                      onChange={(e) => setExcelConfig(prev => ({ ...prev, groupBy: e.target.value }))}
                      className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <option value="client">By Client</option>
                      <option value="project">By Project</option>
                      <option value="user">By User</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-center space-x-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={excelConfig.includeDetails}
                        onChange={(e) => setExcelConfig(prev => ({ ...prev, includeDetails: e.target.checked }))}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 text-green-600 rounded focus:ring-2 focus:ring-green-500"
                      />
                      <span className="text-slate-700">Include drill-down details</span>
                    </label>

                    <label className="flex items-center space-x-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={excelConfig.includeSummary}
                        onChange={(e) => setExcelConfig(prev => ({ ...prev, includeSummary: e.target.checked }))}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 text-green-600 rounded focus:ring-2 focus:ring-green-500"
                      />
                      <span className="text-slate-700">Include summary sheet</span>
                    </label>

                    <label className="flex items-center space-x-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={excelConfig.includeAllViews}
                        onChange={(e) => setExcelConfig(prev => ({ ...prev, includeAllViews: e.target.checked }))}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 text-green-600 rounded focus:ring-2 focus:ring-green-500"
                      />
                      <span className="text-slate-700">All views (Client, Project, User, Role)</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Close button */}
              <div className="p-3 border-t border-slate-200 bg-slate-50 rounded-b-lg">
                <button
                  onClick={() => setShowExportMenu(false)}
                  className="w-full text-sm text-slate-600 hover:text-slate-900 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

     

      {/* Export Settings Modal */}
      {showSettings && (
        <ExportSettingsModal
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
          settings={exportSettings}
          onSave={setExportSettings}
          currentTenant={currentTenant}
        />
      )}
    </div>
  )
}

export default ExportControls